import { HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy, Router } from '@angular/router';
import { SQLite } from '@ionic-native/sqlite/ngx';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { IonicStorageModule } from '@ionic/storage';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import * as SentryAngular from '@sentry/angular';
import { single } from 'rxjs/operators';
import { AppAdapter } from './adapters/app-info/app.adapter';
import { EnvironmentAdapter } from './adapters/environment/environment.adapter';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { GoogleMapsComponentModule } from './components/map/google-map/modules/google-maps-component.module';
import { TranslationLoader } from './loader/translation.loader';
import { AddLoopHelpPageModule } from './pages/browser/add-loop-help/add-loop-help.module';
import { AddStopsHelpPageModule } from './pages/browser/add-stops-help/add-stops-help.module';
import { AddStopsTutorialPageModule } from './pages/browser/add-stops-tutorial/add-stops-tutorial.module';
import { BrowserFeedbackPageModule } from './pages/browser/browser-feedback/browser-feedback.module';
import { BrowserGeocoderPageModule } from './pages/browser/browser-geocoder/browser-geocoder.module';
import { BrowserMenuPageModule } from './pages/browser/browser-menu/browser-menu.module';
import { DayStartEndPageModule } from './pages/browser/day-start-end/day-start-end.module';
import { InstructionsPageModule } from './pages/browser/instructions/instructions.module';
import { StopDefinitionPageModule } from './pages/browser/stop-definition/stop-definition.module';
import { TimeSelectPageModule } from './pages/browser/time-select/time-select.module';
import { TimeTypeinPageModule } from './pages/browser/time-typein/time-typein.module';
import { DeleteReasonPageModule } from './pages/edit/delete-reason/delete-reason.module';
import { StatusLdPageModule } from './pages/ld/status-ld/status-ld.module';
import { AddedTripTypePageModule } from './pages/roster/added-trip-type/added-trip-type.module';
import { CalendarPageModule } from './pages/roster/calendar/calendar.module';
import { FeedbackPageModule } from './pages/roster/feedback/feedback.module';
import { HowToTextPageModule } from './pages/roster/how-to-text/how-to-text.module';
import { HowToPageModule } from './pages/roster/how-to/how-to.module';
import { MenuTermsPageModule } from './pages/roster/menu-terms/menu-terms.module';
import { MenuPageModule } from './pages/roster/menu/menu.module';
import { ContactPageModule } from './pages/setup/contact/contact.module';
import { RequestExactAlarmPageModule } from './pages/setup/exact-alarm/request-exact-alarm.module';
import { LocationRequiredPageModule } from './pages/setup/location-required/location-required.module';
import { SignupDetailPageModule } from './pages/setup/signup-detail/signup-detail.module';
import { SelectsDetailPageModule } from './pages/survey/selects-detail/selects-detail.module';
import { SurveyGeocoderPageModule } from './pages/survey/survey-geocoder/modules/survey-geocoder-page.module';
import { SurveyMapPageModule } from './pages/survey/survey-map/survey-map.module';
import { TransitDetailComponentModule } from './pages/survey/transit-detail/transit-detail.module';
import { TransitMapPageModule } from './pages/survey/transit-map/transit-map.module';
import { VehiclesDetailPageModule } from './pages/survey/vehicles-detail/vehicles-detail.module';
import { INIT_DEV_SERVICES_TOKEN, InitDevService } from './services/dev/dev.interface';
import { DevModule } from './services/dev/dev.module';
import { MockModule } from './services/mock.module';
import { CapsuleModule } from './use-case/feature-flags/feature-flag-capsule.module';
import { RemoteLoggerUseCase } from './use-case/remote-logger/remote-logger.use-case';

function httpLoaderFactory(http: HttpClient, environmentAdapter: EnvironmentAdapter): TranslateLoader {
  return new TranslationLoader(http, environmentAdapter);
}

function appInitFactory(devServices: InitDevService[]): () => Promise<any> {
  return async () => {
    for (const service of devServices) {
      await service.init();
    }

    return Promise.resolve();
  };
}

function tagAppFactory(appAdapter: AppAdapter, remoteLoggerUseCase: RemoteLoggerUseCase): () => Promise<void> {
  return async () => {
    const appInfo = await appAdapter.getAppInfo().pipe(single()).toPromise();

    remoteLoggerUseCase.setTag('app.version', appInfo.version);
    remoteLoggerUseCase.setTag('app.build', appInfo.build);
    remoteLoggerUseCase.setTag('app.id', appInfo.id);
  };
}

@NgModule({
  declarations: [AppComponent],
  entryComponents: [],
  imports: [
    HttpClientModule,
    MockModule,
    BrowserModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient, EnvironmentAdapter]
      }
    }),
    IonicStorageModule.forRoot({
      driverOrder: ['indexeddb', 'websql', 'sqlite']
    }),
    IonicModule.forRoot({
      mode: 'md'
    }),
    AppRoutingModule,
    // ErrorHandlerModule,
    SurveyGeocoderPageModule,
    MenuPageModule,
    FeedbackPageModule,
    HowToPageModule,
    HowToTextPageModule,
    MenuTermsPageModule,
    AddedTripTypePageModule,
    SurveyMapPageModule,
    TransitMapPageModule,
    DayStartEndPageModule,
    AddStopsTutorialPageModule,
    TimeSelectPageModule,
    TimeTypeinPageModule,
    BrowserGeocoderPageModule,
    InstructionsPageModule,
    BrowserFeedbackPageModule,
    BrowserMenuPageModule,
    DeleteReasonPageModule,
    SelectsDetailPageModule,
    VehiclesDetailPageModule,
    TransitDetailComponentModule,
    StopDefinitionPageModule,
    LocationRequiredPageModule,
    RequestExactAlarmPageModule,
    SignupDetailPageModule,
    ContactPageModule,
    AddStopsHelpPageModule,
    AddLoopHelpPageModule,
    CalendarPageModule,
    AddLoopHelpPageModule,
    StatusLdPageModule,
    GoogleMapsComponentModule,
    CapsuleModule,
    DevModule
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: appInitFactory,
      deps: [INIT_DEV_SERVICES_TOKEN],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {
        /* No-op */
      },
      deps: [SentryAngular.TraceService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: tagAppFactory,
      deps: [AppAdapter, RemoteLoggerUseCase],
      multi: true
    },
    SQLite,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: SentryAngular.TraceService, deps: [Router] }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
